import { axios } from '@/utils/axios';
import filenameValidator from '@/utils/filenameValidator';
import CryptoJS from 'crypto-js';

const md5Password = (password?: string | null) => (password ? CryptoJS.MD5(password).toString() : password);

const withMd5Password = <T extends { password?: string | null }>(data: T): T => {
  if (!data.password) return data;

  return {
    ...data,
    password: md5Password(data.password),
  } as T;
};

/**
 * ANCHOR 取得檔案列表
 */
export const getDocument = async () => {
  return axios.get('/api/document');
};

/**
 * ANCHOR 上傳檔案
 */
export const storeDocument = async (data: LibraryDataRequest) => {
  const formData = new FormData();
  for (const [index, iterator] of Object.entries(data.files)) {
    formData.append(`files[${index}]`, iterator as File, filenameValidator(iterator as File));
  }

  return axios.post('/api/document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/**
 * ANCHOR 更新檔案
 */
export const updateDocument = async (id: number, data: []) => {
  return axios.put(`/api/document/${id}`, data);
};

/**
 * ANCHOR 刪除檔案
 */
export const DestroyDocument = async (id: string) => {
  return axios.delete(`/api/document/${id}`);
};

/**
 * ANCHOR Translate
 */
export const Translate = async (data: {
  document_id: string;
  languagePairId: string;
  from: string;
  to: string;
}) => {
  return axios.post<TranslationApiResponse>(`/api/document/translate`, data);
};

export type TranslationJobStatus = 'NotStarted' | 'Running' | 'ResultUnknown' | 'Succeeded' | 'Failed' | 'Expired' | string;

export interface TranslationCapabilities {
  formats: Array<{ extension: string; mime_type: string; output_extension: string; default_mode: 'batch' | 'sync'; max_bytes: number }>;
  auto_detect_source_language: boolean;
  batch_enabled: boolean;
}

export const getTranslationCapabilities = () => axios.get<{success: boolean; data: TranslationCapabilities}>('/api/document/translation-capabilities');

export interface TranslationJobData {
  id: number;
  status: TranslationJobStatus;
  source_lang: string;
  target_lang: string;
  output_document_id: number | null;
}

export interface TranslationOutputDocument {
  id: number;
  file?: {
    id: number;
    name: string;
    extension: string;
  } | null;
}

export interface TranslationResultData {
  job: TranslationJobData;
  output_document: TranslationOutputDocument | null;
}

export interface TranslationApiResponse {
  success: boolean;
  message: string;
  data: TranslationResultData;
}

export interface TranslationLanguageOption {
  name: string;
  code: string;
}

interface TranslationLanguageOptionsResponse {
  success: boolean;
  message: string;
  data: TranslationLanguageOption[];
}

export const getTranslationStatus = async (jobId: number) =>
  axios.get<TranslationApiResponse>(`/api/document/translate/status/${jobId}`);

export const getLatestTranslation = (documentId: string) =>
  axios.get<{ success: boolean; data: TranslationResultData | null }>(`/api/document/translation-latest/${encodeURIComponent(documentId)}`);

export const downloadTranslation = async (jobId: number) =>
  axios.get<Blob>(`/api/document/translate/${jobId}/download`, {
    responseType: 'blob',
  });

/**
 * ANCHOR Transcribe
 */
export const Transcribe = async (data: { document_id: string; locale: string }) => {
  return axios.post(`/api/document/transcribe`, data);
};

/**
 * ANCHOR GetLanguagePair
 */
export const GetLanguagePair = async () => {
  return axios.get<TranslationLanguageOptionsResponse>(`/api/document/getLanguagePair`);
}

/**
 * 文件夾管理 API
 * 依據 documentLibrary.d.ts 型別補全
 */

// 1. 獲取根目錄內容
export const getRootFolderContents = async () => {
  return axios.get('/api/file-manager');
};

// 2. 獲取指定文件夾內容
export const getFolderContents = async (folderId: number | string) => {
  return axios.get(`/api/file-manager/folder/${folderId}`);
};

// 3. 創建文件夾
export const createFolder = async (data: { name: string, parent_id: number | null }) => {
  return axios.post('/api/file-manager/folder', data);
};

// 4. 重命名文件夾
export const renameFolder = async (data: { folderId: number | string, newName: string }) => {
  return axios.put(`/api/file-manager/folder/${data.folderId}`, {name: data.newName});
};

// 5. 刪除文件夾
export const deleteFolder = async (folderId: number | string) => {
  return axios.delete(`/api/file-manager/folder/${folderId}`);
};

// 6. 上傳文件到文件夾
export const downloadFolderZip = (folderId: number) =>
  axios.post<Blob>(`/api/file-manager/folder/${folderId}/download-zip`, {}, { responseType: 'blob' });

export const uploadFilesToFolder = async (data: { files: File[], folderId: number | null }, requestKey?: string) => {
  const formData = new FormData();
  data.files.forEach(file => formData.append('files[]', file));
  if (data.folderId !== null) formData.append('folder_id', String(data.folderId));
  return axios.post('/api/file-manager/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data', ...(requestKey ? { 'Idempotency-Key': requestKey } : {}) }
  });
};

export const getDocumentUploadReceipt = (key: string) => axios.get<{success: boolean; data: {state: 'completed' | 'not_found'; document_id?: number}}>(`/api/file-manager/upload-receipts/${encodeURIComponent(key)}`);

// 7. 移動文件到文件夾
export const moveFileToFolder = async (data: { documentId: number | string, folderId: number | null }) => {
  return axios.put(`/api/file-manager/move-file/${data.documentId}`, { folder_id: data.folderId });
};

// 8. 獲取文件夾樹
export const getFolderTree = async () => {
  return axios.get('/api/file-manager/folder-tree');
};

// 刪除文件
export const deleteFile = async (documentId: number | string) => {
  return axios.delete(`/api/file-manager/file/${documentId}`);
};

// 刪除分享
export const deleteShareRecord = async (shareId: number | string)=> {
  return axios.delete(`/api/shares/${shareId}`);
};

// 分享用戶列表
export const getShareUsers = async (params?: {
  page?: number;
  per_page?: number;
  q?: string;
  name?: string;
  company?: string;
  sort?: string;
  order?: string;
  exclude_self?: boolean;
}) => {
  return axios.get<ShareUserListResponse>('/api/share/users', { params });
};

// 建立分享
export const createShare = async (data: {
  target_type: 'folder' | 'document';
  target_id: number;
  recipient_user_ids: number[];
}) => {
  return axios.post('/api/shares', data);
};

// 撤銷分享
export const deleteShare = async (shareId: number) => {
  return axios.delete(`/api/shares/${shareId}`);
};

// 我的分享列表
export const getMyShares = async (params?: { per_page?: number; page?: number }) => {
  return axios.get('/api/shares/my', { params });
};

// 分享給我列表
export const getSharedToMe = async (params?: { per_page?: number; page?: number }) => {
  return axios.get('/api/shared', { params });
};

// 瀏覽分享內容（資料夾遞進或單檔）
export const getSharedContents = async (shareId: number, folderId?: number) => {
  const params = folderId ? { folder_id: folderId } : {};
  return axios.get(`/api/shared/${shareId}/contents`, { params });
};

/**
 * ============ 公開連結分享 API ============
 * 需要登入的 API
 */

// 1. 創建分享連結
export const createShareLink = async (data: CreateShareLinkRequest) => {
  return axios.post('/api/share-links', withMd5Password(data));
};

// 2. 我的分享連結列表
export const getMyShareLinks = async (params?: { per_page?: number; page?: number }) => {
  return axios.get('/api/share-links/my', { params });
};

// 3. 更新分享連結
export const updateShareLink = async (id: number, data: UpdateShareLinkRequest) => {
  return axios.put(`/api/share-links/${id}`, withMd5Password(data));
};

// 4. 刪除分享連結
export const deleteShareLink = async (id: number) => {
  return axios.delete(`/api/share-links/${id}`);
};

/**
 * 公開訪問 API（無需登入）
 */

// 1. 獲取分享資訊
export const getPublicShareInfo = async (code: string) => {
  return axios.get(`/api/public/share/${code}`);
};

export const downloadPublicShareZip = (code: string, folderId?: number, shareSessionToken?: string) =>
  axios.get<Blob>(`/api/public/share/${encodeURIComponent(code)}/zip`, {
    params: folderId === undefined ? {} : { folder_id: folderId },
    headers: shareSessionToken ? { 'X-Share-Session': shareSessionToken } : {},
    responseType: 'blob',
  });

// 2. 驗證密碼
export const verifySharePassword = async (code: string, password: string) => {
  return axios.post<{ success: boolean; data: { verified: boolean; share_session_token: string; expires_in: number } }>(
    `/api/public/share/${code}/verify`,
    { password: md5Password(password) }
  );
};

// 3. 獲取分享內容（結構與 file-manager 一致）
export const getPublicShareContents = async (
  code: string,
  params?: { folder_id?: number; password?: string; share_session_token?: string }
) => {
  const { password, share_session_token, ...queryParams } = params ?? {};

  return axios.get(`/api/public/share/${code}/contents`, { 
    params: queryParams,
    headers: {
      ...(password ? { 'X-Share-Password': md5Password(password) } : {}),
      ...(share_session_token ? { 'X-Share-Session': share_session_token } : {}),
    }
  });
};

// 4. 獲取導航資訊（麵包屑）
export const getPublicShareNavigation = async (
  code: string,
  params?: { folder_id?: number; password?: string; share_session_token?: string }
) => {
  const { password, share_session_token, ...queryParams } = params ?? {};

  return axios.get(`/api/public/share/${code}/navigation`, { 
    params: queryParams,
    headers: {
      ...(password ? { 'X-Share-Password': md5Password(password) } : {}),
      ...(share_session_token ? { 'X-Share-Session': share_session_token } : {}),
    }
  });
};


// 自動翻譯API
export const translateText = async (data: { text: string; from: string; to: string }) => {
  return axios.post('/api/document/translate-text', data);
};
